import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  MessageSquare,
  FileText,
  GraduationCap,
  TrendingUp,
  User,
  ChevronRight,
  LogOut,
  Users,
  Settings,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  LayoutDashboard,
  Cpu,
  ChevronDown,
  MoreVertical,
  Edit,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useTranslation } from 'react-i18next';
import { chatService } from '../services/chatService';
import { Conversation } from '../schemas/chat';
import { toast } from 'sonner';
import { ConfirmDialog } from './ui/ConfirmDialog';
import logoImage from '../assets/icons/logo.png';

interface MenuItem {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  path: string;
  disabled?: boolean;
}

interface ChatWithCategory {
  id: string;
  title: string;
  conversation: Conversation;
}

interface ChatGroup {
  period: string;
  chats: ChatWithCategory[];
}

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { state: chatState, deleteConversation, updateConversation } = useChat();
  const { t } = useTranslation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAdminSectionCollapsed, setIsAdminSectionCollapsed] = useState(true);
  const [chats, setChats] = useState<ChatWithCategory[]>([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [chatActionMenuOpen, setChatActionMenuOpen] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<ChatWithCategory | null>(null);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [chatToRename, setChatToRename] = useState<ChatWithCategory | null>(null);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const chatListRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const menuItems: MenuItem[] = [
    { icon: Box, label: t('navigation.dataLibrary'), path: '/knowledge' },
    { icon: MessageSquare, label: t('navigation.newConversation'), path: '/chat' },
    { icon: FileText, label: t('navigation.paperWriting'), path: '/paper-writing', disabled: true },
    {
      icon: GraduationCap,
      label: t('navigation.academicExchange'),
      path: '/academic-exchange',
      disabled: true,
    },
    {
      icon: TrendingUp,
      label: t('navigation.trendPrediction'),
      path: '/trend-prediction',
      disabled: true,
    },
  ];

  const adminMenuItems: MenuItem[] = [
    { icon: LayoutDashboard, label: t('navigation.adminDashboard'), path: '/admin' },
    { icon: Users, label: t('navigation.users'), path: '/admin/users' },
    { icon: Settings, label: t('navigation.llmConfig'), path: '/admin/llm-config' },
    { icon: Cpu, label: t('navigation.resources'), path: '/admin/resources' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNewConversation = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  // Function to group chats by time period
  const groupChatsByPeriod = (chats: ChatWithCategory[]): ChatGroup[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Use translation keys as internal keys
    const groups: { [key: string]: ChatWithCategory[] } = {
      today: [],
      yesterday: [],
      past7Days: [],
      past30Days: [],
    };

    const monthlyGroups: { [key: string]: ChatWithCategory[] } = {};

    chats.forEach(chat => {
      const chatDate = new Date(chat.conversation.updated_at);
      const chatTime = chatDate.getTime();

      if (chatTime >= today.getTime()) {
        groups['today'].push(chat);
      } else if (chatTime >= yesterday.getTime()) {
        groups['yesterday'].push(chat);
      } else if (chatTime >= sevenDaysAgo.getTime()) {
        groups['past7Days'].push(chat);
      } else if (chatTime >= thirtyDaysAgo.getTime()) {
        groups['past30Days'].push(chat);
      } else {
        // Group by year-month (e.g., "2025-12")
        const yearMonth = `${chatDate.getFullYear()}-${String(chatDate.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyGroups[yearMonth]) {
          monthlyGroups[yearMonth] = [];
        }
        monthlyGroups[yearMonth].push(chat);
      }
    });

    // Build final array with non-empty groups
    const result: ChatGroup[] = [];

    // Add time-based groups in order using i18n translations
    const periodKeys = ['today', 'yesterday', 'past7Days', 'past30Days'] as const;
    periodKeys.forEach(key => {
      if (groups[key].length > 0) {
        result.push({ period: t(`timePeriods.${key}`), chats: groups[key] });
      }
    });

    // Add monthly groups in reverse chronological order (keep YYYY-MM format as-is)
    Object.keys(monthlyGroups)
      .sort()
      .reverse()
      .forEach(yearMonth => {
        if (monthlyGroups[yearMonth].length > 0) {
          result.push({ period: yearMonth, chats: monthlyGroups[yearMonth] });
        }
      });

    return result;
  };

  // Convert conversations to ChatWithCategory format
  const convertConversations = useCallback((conversations: any[]): ChatWithCategory[] => {
    return conversations.map((conversation: any) => ({
      id: conversation.id,
      title: conversation.title,
      conversation,
    }));
  }, []);

  // Fetch chats from API
  const fetchChats = useCallback(
    async (page: number = 1, append: boolean = false) => {
      try {
        if (append) {
          setLoadingMoreChats(true);
        } else {
          setLoadingChats(true);
          setCurrentPage(1);
          setHasMoreChats(true);
        }

        const PAGE_SIZE = 20;
        const response = await chatService.getConversations(page, PAGE_SIZE, {
          include_archived: false,
        });

        const conversations = (response as any).items || [];
        const categorizedChats = convertConversations(conversations);

        if (append) {
          setChats(prev => [...prev, ...categorizedChats]);
        } else {
          setChats(categorizedChats);
        }

        // Check if there are more chats to load
        const totalCount = (response as any).total || 0;

        // No more chats if we received no items
        if (conversations.length === 0) {
          setHasMoreChats(false);
        } else {
          const currentLoadedCount = append
            ? chats.length + conversations.length
            : conversations.length;
          const hasMore = currentLoadedCount < totalCount;
          setHasMoreChats(hasMore);
        }

        if (append && conversations.length > 0) {
          setCurrentPage(page);
        }
      } catch (error) {
        console.error('Failed to fetch chats:', error);
        if (!append) {
          setChats([]);
        }
      } finally {
        if (append) {
          setLoadingMoreChats(false);
        } else {
          setLoadingChats(false);
        }
      }
    },
    [convertConversations]
  );

  // Load more chats when scrolling to bottom
  const loadMoreChats = useCallback(() => {
    if (hasMoreChats && !loadingMoreChats && !loadingChats) {
      fetchChats(currentPage + 1, true);
    }
  }, [hasMoreChats, loadingMoreChats, loadingChats, currentPage, fetchChats]);

  // Handle scroll event for infinite scroll
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 50 && hasMoreChats && !loadingMoreChats) {
        loadMoreChats();
      }
    },
    [loadMoreChats, hasMoreChats, loadingMoreChats]
  );

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (isSidebarOpen && window.innerWidth < 1024) {
        const sidebar = document.getElementById('sidebar');
        const menuButton = document.getElementById('menu-button');

        if (
          sidebar &&
          !sidebar.contains(event.target as Node) &&
          menuButton &&
          !menuButton.contains(event.target as Node)
        ) {
          setIsSidebarOpen(false);
        }
      }
    };

    // Handle escape key to close sidebar
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    // Close sidebar on window resize to desktop
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('resize', handleResize);
    };
  }, [isSidebarOpen]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleProfileOutsideClick = (event: MouseEvent) => {
      if (
        isProfileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleProfileOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleProfileOutsideClick);
    };
  }, [isProfileMenuOpen]);

  // Handle menu item click - close sidebar on mobile
  const handleMenuClick = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  // Handle rename click
  const handleRenameClick = (chat: ChatWithCategory) => {
    setChatToRename(chat);
    setNewChatTitle(chat.title);
    setShowRenameDialog(true);
    setChatActionMenuOpen(null);
  };

  // Handle rename submit
  const handleRenameSubmit = async () => {
    if (!chatToRename) return;
    try {
      const oldTitle = chatToRename.title;
      await updateConversation(chatToRename.id, { title: newChatTitle });
      // Update local state
      setChats(prev =>
        prev.map(chat => (chat.id === chatToRename.id ? { ...chat, title: newChatTitle } : chat))
      );
      setShowRenameDialog(false);
      setChatToRename(null);
      setNewChatTitle('');
      toast.success(t('chat.renameConversationSuccess', { oldTitle, newTitle: newChatTitle }));
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      // Error is handled by global HTTP error handler (toast notification)
    }
  };

  // Handle delete click
  const handleDeleteClick = (chat: ChatWithCategory) => {
    setChatToDelete(chat);
    setShowDeleteDialog(true);
    setChatActionMenuOpen(null);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!chatToDelete) return;
    setIsDeleting(true);
    try {
      const deletedTitle = chatToDelete.title;
      await deleteConversation(chatToDelete.id);
      // Update local state
      setChats(prev => prev.filter(chat => chat.id !== chatToDelete!.id));
      setShowDeleteDialog(false);
      setChatToDelete(null);
      toast.success(t('chat.deleteConversationSuccess', { title: deletedTitle }));
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // Error is handled by global HTTP error handler (toast notification)
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle long press for mobile
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const handleTouchStart = (chatId: string) => {
    const timer = setTimeout(() => {
      setChatActionMenuOpen(chatId);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Close chat action menu when clicking outside
  useEffect(() => {
    const handleChatActionOutsideClick = (event: MouseEvent) => {
      if (chatActionMenuOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-chat-action-menu]')) {
          setChatActionMenuOpen(null);
        }
      }
    };

    if (chatActionMenuOpen) {
      document.addEventListener('mousedown', handleChatActionOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleChatActionOutsideClick);
    };
  }, [chatActionMenuOpen]);

  useEffect(() => {
    // Only fetch chats if user is authenticated
    if (user) {
      fetchChats();
    }
  }, [user, fetchChats]);

  // Sync sidebar with ChatContext when conversation titles are updated
  useEffect(() => {
    if (!user) {
      return;
    }

    if (chatState.conversations.length === 0) {
      return;
    }

    // Create a map of conversation IDs to their full conversation data from ChatContext
    const chatContextMap = new Map<string, { title: string; conversation: Conversation }>();
    chatState.conversations.forEach(conv => {
      chatContextMap.set(conv.id, {
        title: conv.title,
        conversation: conv,
      });
    });

    // Update local chats state:
    // 1. Update titles for existing chats
    // 2. Add new conversations that aren't in the sidebar yet
    setChats(prevChats => {
      const existingChatIds = new Set(prevChats.map(chat => chat.id));

      // Add new conversations from ChatContext that aren't in sidebar yet
      const newChats = chatState.conversations
        .filter(conv => !existingChatIds.has(conv.id))
        .map(conv => ({
          id: conv.id,
          title: conv.title,
          conversation: conv,
        }));

      // Update titles for existing chats
      const updatedExistingChats = prevChats.map(chat => {
        const chatContextData = chatContextMap.get(chat.id);
        if (chatContextData && chatContextData.title !== chat.title) {
          return { ...chat, title: chatContextData.title };
        }
        return chat;
      });

      // Combine: new chats first (at the top), then updated existing chats
      return [...newChats, ...updatedExistingChats];
    });
  }, [chatState.conversations, user]);

  return (
    <div className='flex h-screen bg-ui-cream relative font-body'>
      {/* Mobile Menu Overlay */}
      {isSidebarOpen && (
        <div
          className='fixed inset-0 bg-ui-deep bg-opacity-50 z-40 lg:hidden backdrop-blur-sm animate-fade-in'
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Mobile Menu Button */}
      <button
        id='menu-button'
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className='lg:hidden fixed top-4 left-4 z-40 p-2 bg-white border border-ui-border rounded-ui-md shadow-ui hover:shadow-ui-md transition-all duration-200'
      >
        {isSidebarOpen ? (
          <X size={20} className='text-ui-charcoal' />
        ) : (
          <Menu size={20} className='text-ui-charcoal' />
        )}
      </button>

      {/* Sidebar */}
      <div
        id='sidebar'
        className={`
          fixed lg:static inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out border-r border-sidebar-border
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isSidebarCollapsed ? 'w-20' : 'w-72'}
          max-w-[80vw] lg:max-w-none
        `}
        style={{
          background: '#FAFAFA',
          boxShadow: '1px 0 8px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Header Section */}
        <div className='p-4 relative group'>
          {/* Logo - centered when collapsed, hidden on hover when collapsed */}
          <div
            className={`transition-all duration-200 ${
              isSidebarCollapsed ? 'flex justify-center' : 'flex justify-start'
            } ${isSidebarCollapsed ? 'group-hover:opacity-0 group-hover:invisible' : ''}`}
          >
            <Link to='/' className='block'>
              <img src={logoImage} alt='NeverEnd AI' className='h-8 w-auto' />
            </Link>
          </div>

          {/* Collapse button (desktop) - hidden when collapsed, shown on hover */}
          {!isSidebarCollapsed && (
            <button
              onClick={e => {
                e.stopPropagation();
                setIsSidebarCollapsed(!isSidebarCollapsed);
              }}
              onMouseDown={e => e.stopPropagation()}
              className='hidden lg:flex absolute top-1/2 -translate-y-1/2 right-4 p-1.5 hover:bg-sidebar-hover rounded-lg transition-all duration-200 text-sidebar-text hover:text-sidebar-text-active hover:shadow-md'
              title='Collapse sidebar'
            >
              <PanelLeftClose size={18} />
            </button>
          )}

          {/* Expand button - shown on hover when collapsed, replaces logo */}
          {isSidebarCollapsed && (
            <button
              onClick={e => {
                e.stopPropagation();
                setIsSidebarCollapsed(!isSidebarCollapsed);
              }}
              onMouseDown={e => e.stopPropagation()}
              className='hidden lg:flex absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 bg-white hover:bg-sidebar-hover rounded-lg shadow-md'
              title='Expand sidebar'
            >
              <PanelLeftOpen size={18} />
            </button>
          )}

          {/* Close button for mobile */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            className='lg:hidden absolute top-1/2 -translate-y-1/2 right-4 p-1.5 hover:bg-sidebar-hover rounded-lg transition-all duration-200 text-sidebar-text hover:text-sidebar-text-active'
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className='px-3 py-4 space-y-1'>
          {menuItems.map(item => {
            const isActive =
              item.path === '/chat' ? location.pathname === '/' : location.pathname === item.path;

            // Special handling for "New Conversation" - navigate to home page
            if (item.path === '/chat') {
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    handleNewConversation();
                    handleMenuClick();
                  }}
                  title={isSidebarCollapsed ? item.label : undefined}
                  className={`flex items-center ${
                    isSidebarCollapsed ? 'justify-center' : 'space-x-3'
                  } px-3 py-2.5 rounded-ui-md transition-all duration-200 w-full text-left font-display font-medium text-sm ${
                    isActive
                      ? 'bg-gradient-to-r from-sidebar-accent to-blue-500 text-white shadow-lg shadow-sidebar-accent/25'
                      : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active'
                  }`}
                >
                  <item.icon size={18} />
                  <span
                    className={`transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
                  >
                    {item.label}
                  </span>
                </button>
              );
            }

            // Handle disabled menu items with "coming soon" label
            if (item.disabled) {
              return (
                <div
                  key={item.path}
                  title={isSidebarCollapsed ? item.label : undefined}
                  className={`${
                    isSidebarCollapsed ? 'justify-center' : 'flex-col items-start'
                  } px-3 py-2 rounded-ui-md font-display font-medium text-sm text-sidebar-text/50 cursor-not-allowed`}
                >
                  <div
                    className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'space-x-3'}`}
                  >
                    <item.icon size={18} />
                    <span
                      className={`transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
                    >
                      {item.label}
                    </span>
                  </div>
                  {!isSidebarCollapsed && (
                    <span className='text-[9px] text-sidebar-accent/60 mt-0.5 ml-7 leading-none'>
                      {t('navigation.comingSoon')}
                    </span>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleMenuClick}
                title={isSidebarCollapsed ? item.label : undefined}
                className={`flex items-center ${
                  isSidebarCollapsed ? 'justify-center' : 'space-x-3'
                } px-3 py-2.5 rounded-ui-md transition-all duration-200 font-display font-medium text-sm ${
                  isActive
                    ? 'bg-gradient-to-r from-sidebar-accent to-blue-500 text-white shadow-lg shadow-sidebar-accent/25'
                    : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active'
                }`}
              >
                <item.icon size={18} />
                <span
                  className={`transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* Admin Section */}
          {user?.role === 'admin' && (
            <div className='pt-4'>
              {/* Collapsible Admin Header */}
              <button
                onClick={() => setIsAdminSectionCollapsed(!isAdminSectionCollapsed)}
                className={`flex items-center w-full ${
                  isSidebarCollapsed ? 'justify-center px-3' : 'justify-between px-3'
                } mb-2 group rounded-ui-md transition-all duration-150`}
                title={isSidebarCollapsed ? t('navigation.admin') : undefined}
              >
                {!isSidebarCollapsed && (
                  <h3 className='text-sidebar-text/60 text-xs font-display font-medium uppercase tracking-wide'>
                    {t('navigation.admin')}
                  </h3>
                )}
                <ChevronDown
                  size={16}
                  className={`text-sidebar-text/60 transition-transform duration-200 ${isAdminSectionCollapsed ? '-rotate-90' : ''}`}
                />
              </button>

              {/* Admin Menu Items - Collapsible */}
              <div
                className={`transition-all duration-200 overflow-hidden ${
                  isAdminSectionCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100'
                }`}
              >
                {adminMenuItems.map(item => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleMenuClick}
                      title={isSidebarCollapsed ? item.label : undefined}
                      className={`flex items-center ${
                        isSidebarCollapsed ? 'justify-center' : 'space-x-3'
                      } px-3 py-2.5 rounded-ui-md transition-all duration-200 font-display font-medium text-sm mb-1 ${
                        isActive
                          ? 'bg-gradient-to-r from-sidebar-accent to-blue-500 text-white shadow-lg shadow-sidebar-accent/25'
                          : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active'
                      }`}
                    >
                      <item.icon size={18} />
                      <span
                        className={`transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
                      >
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* Dialogue History - Hide when collapsed */}
        <div
          className={`px-4 py-4 border-t border-sidebar-border flex flex-col flex-1 min-h-0 transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 overflow-hidden h-0 p-0' : ''}`}
        >
          <div className='mb-3 flex-shrink-0'>
            <h3 className='text-sidebar-text text-xs font-display font-medium uppercase tracking-wide'>
              {t('navigation.dialogueHistory')}
            </h3>
          </div>
          <div
            ref={chatListRef}
            onScroll={handleScroll}
            className='flex-1 h-0 space-y-4 overflow-y-auto pr-1 scrollbar-ui'
          >
            {loadingChats ? (
              <div className='text-center py-3'>
                <div className='inline-flex items-center space-x-2 text-sidebar-text text-sm'>
                  <div className='animate-spin rounded-full h-3 w-3 border-2 border-sidebar-text/20 border-t-sidebar-accent'></div>
                  <span>{t('common.loading')}</span>
                </div>
              </div>
            ) : chats.length > 0 ? (
              <>
                {groupChatsByPeriod(chats).map(group => (
                  <div key={group.period} className='space-y-1.5'>
                    <div
                      className='text-xs text-sidebar-text/50 font-display font-medium uppercase tracking-wide sticky top-0 py-1 z-10'
                      style={{
                        background:
                          'linear-gradient(to bottom, #FAFAFA 0%, #FAFAFA 80%, transparent 100%)',
                      }}
                    >
                      {group.period}
                    </div>
                    {group.chats.map(chat => (
                      <div key={chat.id} className='relative group' data-chat-action-menu>
                        <div
                          className='p-2.5 pr-8 rounded-ui-md text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active cursor-pointer transition-all duration-200 text-sm'
                          onClick={() => {
                            navigate(`/chat/${chat.id}`, { state: null });
                            handleMenuClick();
                          }}
                          onTouchStart={() => handleTouchStart(chat.id)}
                          onTouchEnd={handleTouchEnd}
                        >
                          <div className='truncate group-hover:text-sidebar-text-active transition-colors font-body'>
                            {chat.title}
                          </div>
                        </div>

                        {/* Action menu button - visible on hover for desktop, always visible on mobile when triggered by long press */}
                        <div className='absolute bottom-1 right-1'>
                          <button
                            className={`p-1 hover:bg-gray-200 rounded-lg transition-all duration-200 ${
                              chatActionMenuOpen === chat.id
                                ? 'opacity-100 bg-gray-200'
                                : 'lg:opacity-0 lg:invisible lg:group-hover:opacity-100 lg:group-hover:visible'
                            }`}
                            onClick={e => {
                              e.stopPropagation();
                              setChatActionMenuOpen(
                                chatActionMenuOpen === chat.id ? null : chat.id
                              );
                            }}
                            aria-label={`Actions for ${chat.title}`}
                          >
                            <MoreVertical size={14} className='text-gray-500' />
                          </button>

                          {/* Action Menu Dropdown */}
                          {chatActionMenuOpen === chat.id && (
                            <div
                              className='absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] z-20'
                              onClick={e => e.stopPropagation()}
                            >
                              <button
                                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2'
                                onClick={() => handleRenameClick(chat)}
                              >
                                <Edit size={14} />
                                {t('chat.renameConversation')}
                              </button>
                              <div className='border-t border-gray-100 my-1' />
                              <button
                                className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-red-600'
                                onClick={() => handleDeleteClick(chat)}
                              >
                                <Trash2 size={14} />
                                {t('chat.deleteConversation')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                {loadingMoreChats && (
                  <div className='text-center py-2'>
                    <div className='inline-flex items-center space-x-2 text-sidebar-text/70 text-xs'>
                      <div className='animate-spin rounded-full h-2.5 w-2.5 border border-sidebar-text/20 border-t-sidebar-accent'></div>
                      <span>{t('navigation.loadingMore')}</span>
                    </div>
                  </div>
                )}
                {!hasMoreChats && chats.length > 0 && (
                  <div className='text-center py-2'>
                    <div className='text-xs text-sidebar-text/50'>
                      {t('navigation.allConversationsLoaded')}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className='text-center py-4'>
                <div className='text-xs text-sidebar-text/70'>
                  {t('navigation.noConversationHistory')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Profile - Simplified when collapsed */}
        <div className='p-4 border-t border-sidebar-border'>
          <div className='relative' ref={profileMenuRef}>
            <button
              onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              data-testid='user-menu'
              className={`flex items-center ${
                isSidebarCollapsed ? 'justify-center' : 'space-x-3'
              } w-full hover:bg-sidebar-hover rounded-ui-md p-2 transition-all duration-200 group`}
              title={isSidebarCollapsed ? user?.full_name || 'User' : undefined}
            >
              <div className='w-9 h-9 bg-gradient-to-br from-sidebar-accent to-blue-500 rounded-ui-md flex items-center justify-center flex-shrink-0 group-hover:shadow-lg group-hover:shadow-sidebar-accent/25 transition-all duration-200 overflow-hidden'>
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.full_name}
                    className='w-full h-full object-cover rounded-ui-md'
                  />
                ) : (
                  <User size={16} className='text-white' />
                )}
              </div>
              <div
                className={`flex-1 text-left min-w-0 transition-all duration-200 ${isSidebarCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}
              >
                <div className='text-sidebar-text-active text-sm font-display font-medium truncate'>
                  {user?.full_name || '王晓路'}
                </div>
                <div className='text-sidebar-text/70 text-xs truncate font-body'>
                  {user?.role === 'admin'
                    ? t('navigation.roleAdmin')
                    : user?.role === 'user'
                      ? t('navigation.roleUser')
                      : user?.role || t('navigation.roleUser')}
                </div>
              </div>
              {!isSidebarCollapsed && (
                <ChevronRight
                  size={16}
                  className='text-sidebar-text/50 flex-shrink-0 group-hover:text-sidebar-text-active transition-colors'
                />
              )}
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileMenuOpen && (
              <div
                className={`absolute bottom-full mb-2 bg-white border border-ui-border rounded-ui-md shadow-ui-lg py-1 z-50 lg:z-40 animate-slide-up min-w-[200px] ${
                  isSidebarCollapsed ? 'left-full ml-2' : 'left-0 right-0'
                }`}
              >
                <Link
                  to='/settings'
                  className='flex items-center space-x-3 px-4 py-2.5 text-sm text-ui-charcoal hover:bg-ui-light transition-colors font-display font-medium'
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    handleMenuClick();
                  }}
                >
                  <Settings size={16} className='text-ui-primary' />
                  <span>{t('navigation.settings')}</span>
                </Link>
                <div className='border-t border-ui-border my-1'></div>

                <button
                  onClick={() => {
                    handleLogout();
                    handleMenuClick();
                  }}
                  data-testid='logout-button'
                  className='flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-display font-medium w-full'
                >
                  <LogOut size={16} />
                  <span>{t('auth.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex flex-col bg-ui-cream lg:ml-0 overflow-hidden'>
        <Outlet />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setChatToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('chat.deleteConversation')}
        message={t('chat.confirmDeleteConversation')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant='danger'
        loading={isDeleting}
        data-testid='delete-chat-dialog'
      />

      {/* Rename Dialog */}
      {showRenameDialog && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50'
          onClick={() => {
            setShowRenameDialog(false);
            setChatToRename(null);
            setNewChatTitle('');
          }}
        >
          <div
            className='relative bg-white rounded-lg shadow-xl w-full max-w-md p-6'
            onClick={e => e.stopPropagation()}
          >
            <h2 className='text-xl font-semibold text-gray-900 mb-4'>
              {t('chat.renameConversation')}
            </h2>
            <input
              type='text'
              value={newChatTitle}
              onChange={e => setNewChatTitle(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleRenameSubmit();
                } else if (e.key === 'Escape') {
                  setShowRenameDialog(false);
                  setChatToRename(null);
                  setNewChatTitle('');
                }
              }}
              className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500'
              autoFocus
              ref={input => {
                if (input) input.focus();
              }}
            />
            <div className='flex justify-end space-x-3 mt-6'>
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setChatToRename(null);
                  setNewChatTitle('');
                }}
                className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={!newChatTitle.trim()}
                className='px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
